import React, { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import { getCurrentDateInKarachi, formatKarachiDate } from "../utils/dateUtils";

export default function DriverDashboard() {
  const [trips, setTrips] = useState([]);
  const [date, setDate] = useState(() => getCurrentDateInKarachi());
  const [loading, setLoading] = useState(true);
  const [selectedTripId, setSelectedTripId] = useState(null);
  const [announcements, setAnnouncements] = useState([]);

  const load = useCallback(
    async (d = date) => {
      setLoading(true);
      try {
        const data = await api(`/trips/driver?date=${d}`);
        setTrips(data.trips);
        const announcementsRes = await api("/drivers/announcements");
        setAnnouncements(announcementsRes.announcements);
      } catch (e) {
        alert(e.message || "Failed to load trips");
      } finally {
        setLoading(false);
      }
    },
    [date]
  );

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="grid cols-2">
      <div className="card" style={{ gridColumn: "1 / -1" }}>
        {announcements.length > 0 && (
          <div style={{ marginBottom: "1.5rem" }}>
            {announcements.map((announcement) => (
              <div
                key={announcement._id}
                style={{
                  padding: "1rem",
                  background: "#fef3c7",
                  border: "2px solid #f59e0b",
                  borderRadius: "8px",
                  marginBottom: "0.75rem",
                  whiteSpace: "pre-wrap",
                }}
              >
                <strong style={{ color: "#92400e" }}>📢 Announcement:</strong>
                <p style={{ margin: "0.5rem 0 0 0", color: "#78350f" }}>{announcement.message}</p>
              </div>
            ))}
          </div>
        )}
        <h3>Your Trips</h3>

        <div
          style={{
            marginBottom: "1rem",
            display: "flex",
            gap: "1rem",
            alignItems: "center",
          }}
        >
          <input
            type="date"
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              setSelectedTripId(null);
              load(e.target.value);
            }}
            style={{
              padding: "0.5rem",
              fontSize: "0.875rem",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
            }}
          />
          <button className="btn" onClick={() => load(date)}>
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="mt-sm">Loading...</div>
        ) : selectedTripId ? (
          (() => {
            const trip = trips.find((t) => t._id === selectedTripId);
            if (!trip) return <p>Trip not found</p>;

            const timestampLabel = trip.updatedAt && trip.updatedAt !== trip.createdAt ? "Updated At" : "Created At";
            const timestampValue = trip.updatedAt && trip.updatedAt !== trip.createdAt ? trip.updatedAt : trip.createdAt;
            const formattedTimestamp = timestampValue ? formatKarachiDate(timestampValue, {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            }) : "";

            return (
              <div>
                <button
                  className="btn"
                  style={{ marginBottom: "1rem" }}
                  onClick={() => setSelectedTripId(null)}
                >
                  ← Back to All Trips
                </button>

                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    padding: "1.5rem",
                    marginBottom: "1.5rem",
                    background: "#f9fafb",
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      marginBottom: 8,
                      fontSize: "0.9rem",
                      color: "#6b7280",
                    }}
                  >
                    {formatKarachiDate(trip.date)}
                  </p>
                  <h4 style={{ margin: 0, marginBottom: 8 }}>
                    {trip.type.charAt(0).toUpperCase() + trip.type.slice(1)}{" "}
                    Trip
                  </h4>
                  {formattedTimestamp && (
                    <p style={{ fontSize: "0.75rem", color: "#9ca3af", margin: "0.5rem 0" }}>
                      {timestampLabel}: {formattedTimestamp}
                    </p>
                  )}
                  <p
                    style={{
                      margin: 0,
                      fontSize: "0.875rem",
                      color: "#6b7280",
                    }}
                  >
                    Vehicle: {trip.car.name} {trip.car.number} • Capacity:{" "}
                    {trip.car.capacity}
                  </p>
                  <p
                    style={{
                      margin: "0.5rem 0 0 0",
                      fontSize: "0.875rem",
                      color: "#6b7280",
                    }}
                  >
                    Passengers: {trip.passengersCount} / {trip.car.capacity}
                  </p>
                  {trip.passengersCount > trip.car.capacity && (
                    <div
                      style={{
                        marginTop: "0.5rem",
                        padding: "0.5rem",
                        background: "#fef2f2",
                        border: "1px solid #ef4444",
                        borderRadius: "4px",
                        color: "#b91c1c",
                        fontSize: "0.875rem",
                      }}
                    >
                      <strong>Warning:</strong> Passenger count exceeds
                      capacity!
                    </div>
                  )}
                  {trip.car.note && (
                    <div
                      style={{
                        marginTop: "0.75rem",
                        padding: "0.75rem",
                        background: "#f3f4f6",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        fontSize: "0.875rem",
                        color: "#374151",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      <strong>Note:</strong> {trip.car.note}
                    </div>
                  )}
                </div>
              </div>
            );
          })()
        ) : trips.length === 0 ? (
          <div className="empty">
            No trips scheduled for {formatKarachiDate(date)}
          </div>
        ) : (
          <div className="scroll-x">
            <table className="table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Vehicle</th>
                  <th>Capacity</th>
                  <th>Passengers Count</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {trips.map((t) => (
                  <tr key={t._id}>
                    <td>
                      <span
                        className="badge"
                        style={{
                          background:
                            t.type === "morning" ? "#2d7a64" : "#1b4d3e",
                          color: "#fff",
                        }}
                      >
                        {t.type.charAt(0).toUpperCase() + t.type.slice(1)}
                      </span>
                    </td>
                    <td>
                      {t.car.name} {t.car.number}
                    </td>
                    <td>{t.car.capacity}</td>
                    <td>
                      <span
                        style={{
                          color:
                            t.passengersCount > t.car.capacity
                              ? "#dc2626"
                              : "inherit",
                          fontWeight:
                            t.passengersCount > t.car.capacity
                              ? "bold"
                              : "normal",
                        }}
                      >
                        {t.passengersCount}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn"
                        style={{ fontSize: "0.8rem", padding: "0.3rem 0.7rem" }}
                        onClick={() => setSelectedTripId(t._id)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
